import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getPostLoginRoute } from '@/lib/landingRoute';
import { decideGuard } from '@/lib/routeGuard';
import { usePromoPending } from '@/lib/pendingPromo';

// Route guards, moved out of App.jsx so they can be rendered in tests on a real
// hostname. On localhost (dev builds only) both let everything through, which
// is exactly what hid the promo code bug: Login's own redeem effect only ever
// ran because this bypass kept Login mounted after sign-in.

const isLocalDevHost = () =>
  import.meta.env.DEV && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    /^192\.168\.\d+\.\d+$/.test(window.location.hostname)
  );

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-purple-400 to-orange-300">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D8795A]"></div>
  </div>
);

// Wraps routes that require a session, and optionally a subscription.
// The decision itself is src/lib/routeGuard.js.
export const ProtectedRoute = ({ children, skipAuth = false, requireSubscription = false }) => {
  const { user, loading, hasActiveSubscription, subscriptionLoading, hadExpiredPromoTrial } = useAuth();

  // skipAuth only unlocks the localhost bypass, and that is gated on
  // import.meta.env.DEV, so production builds never bypass auth.
  const isDev = isLocalDevHost() && skipAuth;

  const decision = decideGuard({
    loading,
    user,
    isDev,
    requireSubscription,
    subscriptionLoading,
    hasActiveSubscription,
    hadExpiredPromoTrial,
  });

  if (decision.kind === 'spinner') return <Spinner />;
  if (decision.kind === 'redirect') return <Navigate to={decision.to} replace />;
  return children;
};

// Landing and login: a signed-in visitor is sent on to where a login lands.
export const PublicEntryRoute = ({ children }) => {
  const { user, loading, hasActiveSubscription, subscriptionLoading, hasCompletedOnboarding } = useAuth();
  const location = useLocation();
  const promoPending = usePromoPending();

  // Localhost-only: always allow access to public entry routes like landing/login.
  if (isLocalDevHost()) return children;

  if (loading || subscriptionLoading) return <Spinner />;

  if (!user) return children;

  // Allow logged-in users to browse the landing when navigating from inside the app
  if (location.state?.fromApp) return children;

  // A code typed before sign-in is being redeemed (PendingCodeRedeemer). Hold
  // the redirect: the redeemer routes the person once the server has answered.
  if (promoPending) return <Spinner />;

  // Same answer as Login.jsx and AuthCallback.jsx.
  return <Navigate to={getPostLoginRoute({ user, hasActiveSubscription, hasCompletedOnboarding })} replace />;
};
